import jwtDecode from 'jwt-decode';
import { AuthServiceBase } from './service-auth-base';
import { isValidJwt } from './utils-auth';
import { isMsx } from '../../utils/utils-msx';

export const MSX_AUTH_NOT_FOUND = 'MSXAuthService: MsxAuthenticationService not found';
export const MSX_AUTH_INVALID_TOKEN = 'MSXAuthService: Invalid JWT token received';

/**
 * MSX is the authoritative token source when embedded, but the host injects
 * window.MsxAuthenticationService asynchronously (it can take several seconds to appear on a
 * cold load) and can fail transiently. Rather than immediately abandoning MSX (MSAL cannot
 * recover a session in the embedded context — its cross-origin popup bridge is undeliverable),
 * we wait for the host to become ready and retry token acquisition with backoff so a slow or
 * flaky MSX session self-heals silently. These waits/retries are MSX-only; in standalone the
 * MSX service is absent and we fail fast so the app falls back to MSAL immediately.
 */
const MSX_READY_TIMEOUT_MS = 10000;
const MSX_READY_POLL_MS = 250;
const MSX_TOKEN_MAX_RETRIES = 3;
const MSX_TOKEN_RETRY_BASE_MS = 400;

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

const getMsxService = async (): Promise<any> => {
  // Standalone (or any non-MSX host): do not wait — the MSX service should not be used here.
  if (!isMsx()) {
    return (window as any)?.MsxAuthenticationService;
  }
  // Embedded in MSX: the host object can lag the initial render; poll until it is available.
  const deadline = Date.now() + MSX_READY_TIMEOUT_MS;
  let service = (window as any)?.MsxAuthenticationService;
  while (!service && Date.now() < deadline) {
    await sleep(MSX_READY_POLL_MS);
    service = (window as any)?.MsxAuthenticationService;
  }
  return service;
};

export class MSXAuthService extends AuthServiceBase {
  public getAccountInfo = (): Promise<any> =>
    new Promise((resolve, reject) => {
      this.getTokenForScope(this.GRAPH_AUTH_SCOPE).catch((error: Error) => {
        reject(error);
      });
      this.getTokenForScope(this.MEM_AUTH_SCOPE)
        .then((token: string) => {
          if (!isValidJwt(token)) {
            throw new Error(MSX_AUTH_INVALID_TOKEN);
          }
          const decoded: { upn?: string } = jwtDecode(token);
          resolve(decoded.upn);
        })
        .catch((error: Error) => {
          reject(error);
        });
    });

  public getTokenForScope = (scope: string): Promise<string> =>
    new Promise((resolve, reject) => {
      getMsxService()
        .then(async (service: any) => {
          if (!service) {
            reject(new Error(MSX_AUTH_NOT_FOUND));
            return;
          }
          // In MSX, retry transient host failures with exponential backoff so a stale/slow
          // session recovers silently. In standalone this loop runs once (maxRetries = 0).
          const maxRetries = isMsx() ? MSX_TOKEN_MAX_RETRIES : 0;
          let lastError: unknown;
          for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
            try {
              const token: string = await service.acquireTokenForScope(scope);
              resolve(token);
              return;
            } catch (error) {
              lastError = error;
              if (attempt < maxRetries) {
                await sleep(MSX_TOKEN_RETRY_BASE_MS * 2 ** attempt);
              }
            }
          }
          reject(lastError instanceof Error ? lastError : new Error(String(lastError)));
        })
        .catch((error: Error) => {
          reject(error);
        });
    });
}
